import {
  IDeliveryAddress,
  IExtendedArticleLine,
  IIntegratedCare,
  ISingleOrder,
  ITrackingInfo,
  OrderStatus,
  TransmissionStatus,
} from '@alberta/konexi-shared';
import { groupBy } from 'lodash';
import moment from 'moment';
import { addBusinessDays } from 'src/app/common/date/business-days';
import { getDeliveryEndDate, getStartAndEndDatesForMonthlyOrder } from 'src/app/common/date/order-dates';
import { DoctorDB, InsuranceContractDB, PharmacyDB } from 'src/app/common/repository/databases';
import { createObject } from 'src/app/common/tracking/object-creator';
import { ignore, removeTime, tracked } from 'src/app/common/tracking/tracker';
import { BaseViewModel } from 'src/app/common/viewmodel/base-view-model';
import { DoctorDto } from 'src/app/shared/models/institution/doctor-dto.model';
import { PharmacyDto } from 'src/app/shared/models/institution/pharmacy-dto.model';
import { QueryService } from 'src/app/shared/services/query/query.service';
import { TenantConfigService } from 'src/app/shared/services/tenant-config/tenant-config.service';

import { IOrderMaximumQuantity } from '../order/contracts/order-maximum-quantity';
import { getContractAndStandardCareProposal, loadArticlesOfArticleLines } from '../order/order-functions';
import {
  getMaxQuantityForOrderedArticleLine,
  getOrderMaxQuantityForProposedArticleLine,
  updateMaxQuantityInOrderedArticleLines,
} from '../order/order-max-quantity-functions';
import { SingleOrderedArticleLineViewModel } from './single-ordered-articleline-view-model';

export class SingleOrderViewModel extends BaseViewModel implements ISingleOrder {
  isHospitalPrescriber: boolean;
  resmedTenant: string;
  regionId: string;
  salesId: string;
  patientId: string;
  integratedCareId: string;
  creationDate: Date;
  @removeTime()
  deliveryDate: Date;
  @removeTime()
  reminderDate: Date;
  accountingMonth: number[];
  @removeTime()
  startDate: Date = new Date();
  @removeTime()
  endDate: Date = new Date();
  deliveryAddress: IDeliveryAddress;
  deliveryInformation: string;
  doctorId: string;
  pharmacyId: string;
  therapyId: number;
  therapyTypeId: number;
  location: number;
  expressDelivery: boolean;
  isInitialCare: boolean;
  @tracked({ factory: createObject(SingleOrderedArticleLineViewModel) })
  orderedArticleLines: SingleOrderedArticleLineViewModel[] = [];
  createdBy: string;
  status: OrderStatus;
  annotation: string;
  deliveryNote: string;
  deliveryStartTime: string;
  @ignore()
  isNew: boolean;
  @ignore()
  public scope: number;
  @ignore()
  integratedCare: IIntegratedCare;
  @ignore()
  doctorName = 'Unbekannt';
  @ignore()
  pharmacyName = 'Unbekannt';
  deliveryDateUnknown: boolean;
  hospitalId: string;
  prescriptionStatus: TransmissionStatus;
  updated: Date;
  createdAt: Date;
  updatedAt: Date;
  updatedBy: string;
  @ignore()
  selected: boolean;
  tags: string[];
  groupOrderId: string;
  isGroupOrder: boolean;
  orderNumber: string;
  attachments: string[];
  erpOrderNumber: string;
  externalReference: string;
  transmittedAt: string;
  hasFollowUpOrder: boolean;
  auditIds: string[];
  trackingInfo: ITrackingInfo[];
  deliveryOption: string;

  public get startDateVM(): Date {
    return this.startDate;
  }
  public set startDateVM(date: Date) {
    this.startDate = date;
    this.updateScope();
  }
  public get endDateVM(): Date {
    return this.endDate;
  }
  public set endDateVM(date: Date) {
    this.endDate = date;
    this.updateScope();
  }

  constructor() {
    super();
    this.isHospitalPrescriber = false;
  }

  public async initializeNew(
    integratedCare: IIntegratedCare,
    queryService: QueryService,
    tenantConfigService: TenantConfigService
  ) {
    this.integratedCare = integratedCare;
    this.attachments = [];
    this.auditIds = [];
    await this.initializeVariablesFromIntegratedCare(integratedCare, tenantConfigService);
    if (integratedCare.isMonthlyOrderActivated) {
      this.overwriteMonthlyOrderDates();
    }
    const groupedProposedArticleLines = groupBy(integratedCare.proposedArticleLines, (item: IExtendedArticleLine) => {
      return `${item?.contractId} ${item?.therapyProductGroupId}`;
    });
    if (groupedProposedArticleLines) {
      for (const key of Object.keys(groupedProposedArticleLines)) {
        const groupedProposedArticleLinesEntries = groupedProposedArticleLines[key];
        const firstProposedArticleLine = groupedProposedArticleLinesEntries[0];
        const [contract, standardCareProposal] = await getContractAndStandardCareProposal(
          (integratedCare || { standardCareProposalId: null }).standardCareProposalId,
          (firstProposedArticleLine || { contractId: null }).contractId,
          queryService
        );

        let maxQuantity: IOrderMaximumQuantity = null;
        maxQuantity = getOrderMaxQuantityForProposedArticleLine(
          contract,
          firstProposedArticleLine,
          standardCareProposal,
          this.scope
        );

        const articles = await loadArticlesOfArticleLines(groupedProposedArticleLinesEntries, queryService);
        for (const proposedArticleLine of groupedProposedArticleLinesEntries) {
          const articleLine = new SingleOrderedArticleLineViewModel();
          const initResult = articleLine.initialize(
            proposedArticleLine,
            articles.find(article => article._id === proposedArticleLine.articleId),
            this.scope,
            contract,
            maxQuantity
          );
          if (initResult !== null) {
            this.orderedArticleLines.push(articleLine);
          }
        }
      }
    }

    await this.setDoctorName(queryService);
    await this.setPharmacyName(queryService);

    this.isInitialCare = true;
    if (integratedCare.isInitialCare !== undefined) {
      this.isInitialCare = integratedCare.isInitialCare;
    }
  }

  public async initializeExisting(
    integratedCare: IIntegratedCare,
    queryService: QueryService,
    loadMissingArticlesFromCareproposal?: boolean
  ) {
    if (!integratedCare) {
      throw new Error('No  related integratedCare found!');
    }
    this.integratedCare = integratedCare;

    this.scope = this.calcScope();

    await this.populateExistingArticleLines(queryService);
    if (loadMissingArticlesFromCareproposal) {
      await this.addNewArticlesFromIntegratedCare(integratedCare, queryService);
    }
    this.setArticleNotFound(integratedCare);
    await this.initMaxAmount(integratedCare, queryService);
    await this.setDoctorName(queryService);
    await this.setPharmacyName(queryService);

    this.scope = null;
    this.updateScope();
  }

  public async initMaxAmount(integratedCare: IIntegratedCare, queryService: QueryService) {
    const groupByContractIdAndTherapyProductGroupId = (item: SingleOrderedArticleLineViewModel) =>
      `${item.contractId} ${item.article ? item.article.productGroupId : undefined}`;
    const groupedArticlesLines = groupBy(this.orderedArticleLines, groupByContractIdAndTherapyProductGroupId);
    for (const key of Object.keys(groupedArticlesLines)) {
      const orderedArticleLines = groupedArticlesLines[key];
      const firstOrderedArticleLine = orderedArticleLines[0];
      const [contract, standardCareProposal] = await getContractAndStandardCareProposal(
        (integratedCare || { standardCareProposalId: null }).standardCareProposalId,
        (firstOrderedArticleLine || { contractId: null }).contractId,
        queryService
      );

      const maxQuantity = getMaxQuantityForOrderedArticleLine(
        contract,
        firstOrderedArticleLine,
        standardCareProposal,
        integratedCare,
        this.scope
      );

      updateMaxQuantityInOrderedArticleLines.call(this, orderedArticleLines, contract, maxQuantity, {
        setContract: (index, insuranceContract) => (this.orderedArticleLines[index].contract = insuranceContract),
        setCurrentQuantity: index => {
          this.orderedArticleLines[index].maxAmount.currentQuantity +=
            +this.orderedArticleLines[index].quantity * this.orderedArticleLines[index].packagingUnit.quantity;
        },
      });
    }
  }

  public clean() {
    for (let index = this.orderedArticleLines.length - 1; index >= 0; index--) {
      if (!this.orderedArticleLines[index].active) {
        this.orderedArticleLines.splice(index, 1);
      }
    }
  }

  public setDeliveryTimeFrame() {
    const today = new Date().setHours(0, 0, 0, 0);
    if (!this.deliveryDate || this.deliveryDate.setHours(0, 0, 0, 0) === today) {
      this.deliveryDate = addBusinessDays(new Date(), 2);
    }
    this.startDate = this.deliveryDate;
    this.endDate = getDeliveryEndDate(this.startDate, this.therapyId);
  }

  public getDeliveryStartDate() {
    if (this.deliveryStartTime) {
      return moment(`01.01.1970 ${this.deliveryStartTime}`, 'DD.MM.YYYY HH:mm').utc().toDate();
    }
  }
  public setDeliveryStartTime(startTime: Date) {
    if (startTime) {
      this.deliveryStartTime = moment(startTime).format('HH:mm');
    }
  }

  private async setDoctorName(queryService: QueryService) {
    if (!this.doctorId) {
      return;
    }
    const doctor = await queryService.get<DoctorDto>(this.doctorId, DoctorDB);
    if (doctor) {
      this.doctorName = `${doctor.firstName} ${doctor.lastName}`;
    }
  }

  private async setPharmacyName(queryService: QueryService) {
    if (!this.pharmacyId) {
      return;
    }
    const pharmacy = await queryService.get<PharmacyDto>(this.pharmacyId, PharmacyDB);
    if (pharmacy) {
      this.pharmacyName = pharmacy.name;
    }
  }
  /**
   * For Monthly Orders, the start and end dates are calculated based on the current date.
   * The delivery date is set to undefined.
   */
  private overwriteMonthlyOrderDates() {
    const calulatedMonthlyOrderDates = getStartAndEndDatesForMonthlyOrder();
    this.startDate = calulatedMonthlyOrderDates.startDate;
    this.endDate = calulatedMonthlyOrderDates.endDate;
    this.deliveryDate = undefined;
  }

  private async initializeVariablesFromIntegratedCare(
    integratedCare: IIntegratedCare,
    tenantConfigService: TenantConfigService
  ) {
    this.integratedCareId = integratedCare._id;
    this.therapyId = integratedCare.therapyId;
    this.therapyTypeId = integratedCare.therapyTypeId;
    if (integratedCare.location) {
      this.location = integratedCare.location;
    }
    this.status = OrderStatus.Created;
    this.creationDate = new Date();
    this.regionId = integratedCare.regionId;
    if (await tenantConfigService.mustSetDeliveryDate()) {
      this.deliveryDate = this.startDate = addBusinessDays(new Date(), 2);
      if (this.startDate && this.therapyId) {
        this.endDate = getDeliveryEndDate(this.startDate, this.therapyId);
        this.scope = this.calcScope();
      } else {
        throw new Error('Cant calculate Scope because startDate or therapyId is missing');
      }
    } else {
      this.scope = 28;
    }

    this.patientId = integratedCare.patientId;
    this.doctorId = integratedCare.doctorId;
    if (integratedCare.pharmacyId) {
      this.pharmacyId = integratedCare.pharmacyId;
    }
  }

  private async populateExistingArticleLines(queryService: QueryService): Promise<void> {
    const articles = await loadArticlesOfArticleLines(this.orderedArticleLines, queryService);

    for (const orderedArticleLine of this.orderedArticleLines) {
      const article = articles.find(article1 => article1._id === orderedArticleLine.articleId);

      orderedArticleLine.isNew = false;
      orderedArticleLine.active = true;
      if (!article) {
        continue;
      }

      orderedArticleLine.article = article;
      orderedArticleLine.associatedArticle = article;
      if (article) {
        orderedArticleLine.packagingUnit = article.packagingUnits.find(
          pu => pu._id === orderedArticleLine.packagingUnitId && !pu.archived
        );
      }
    }
  }

  private async addNewArticlesFromIntegratedCare(
    integratedCare: IIntegratedCare,
    queryService: QueryService
  ): Promise<void> {
    const articles = await loadArticlesOfArticleLines(integratedCare.proposedArticleLines, queryService);

    for (const proposedArticleLine of integratedCare.proposedArticleLines) {
      if (
        proposedArticleLine &&
        !this.orderedArticleLines.find(orderedLine => orderedLine.articleId === proposedArticleLine.articleId)
      ) {
        const newArticleLine = new SingleOrderedArticleLineViewModel();
        const initResult = newArticleLine.initialize(
          proposedArticleLine,
          articles.find(article => article._id === proposedArticleLine.articleId),
          this.calcScope(),
          await queryService.get(proposedArticleLine.contractId, InsuranceContractDB)
        );
        if (initResult === null) {
          continue;
        }
        newArticleLine.isNew = true;
        newArticleLine.active = false;
        newArticleLine.notInIntegratedCare = false;
        this.orderedArticleLines.push(newArticleLine);
      }
    }
  }
  private setArticleNotFound(integratedCare: IIntegratedCare) {
    if (!integratedCare.proposedArticleLines) {
      return;
    }
    this.orderedArticleLines.forEach(orderedArticleLine => {
      const orderedArticleLineFoundInProposedArticleLines = integratedCare.proposedArticleLines.some(
        proposedArticleLine => proposedArticleLine.articleId === orderedArticleLine.articleId
      );
      orderedArticleLine.notInIntegratedCare = orderedArticleLineFoundInProposedArticleLines === false;
    });
  }

  private calcScope(): number {
    if (this.startDate && this.endDate) {
      return Math.floor(
        Math.abs(new Date(this.endDate).getTime() - new Date(this.startDate).getTime()) / (1000 * 60 * 60 * 24)
      );
    }
  }

  private updateScope() {
    const result = this.calcScope();
    if (result && result !== this.scope) {
      this.scope = result;
      this.updateScopeInOrderedArticleLines();
    }
  }

  private updateScopeInOrderedArticleLines() {
    if (!this.integratedCare || !this.integratedCare.proposedArticleLines) {
      throw new Error('No associated integratedCare found. Please initizale the viewmodel with one');
    }
    this.orderedArticleLines.forEach(orderArticleLine => {
      const proposedArticleLine = this.integratedCare.proposedArticleLines.find(
        articleLine => articleLine?.articleId === orderArticleLine.articleId
      );

      if (proposedArticleLine) {
        orderArticleLine.update(proposedArticleLine, this.scope);
      }
    });
  }
}
